import { useMemo, useRef } from "react";
import { List } from "react-window";
import { Activity, Clock, Link2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ROW_HEIGHT = 76;       // standard activity row
const HEADER_HEIGHT = 36;    // sticky-style day header

function formatDayLabel(isoDate) {
  const d = new Date(isoDate);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return "Today";
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (
    d.getFullYear() === yest.getFullYear() &&
    d.getMonth() === yest.getMonth() &&
    d.getDate() === yest.getDate()
  ) {
    return "Yesterday";
  }
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function groupRows(activities) {
  // Returns a flat array of {kind:"header"|"item", ...} suitable for react-window.
  const rows = [];
  let currentKey = null;
  let bucketCount = 0;
  let lastHeaderIdx = -1;
  for (const a of activities) {
    const ts = a.timestamp || new Date().toISOString();
    const date = new Date(ts);
    const dayKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    if (dayKey !== currentKey) {
      if (lastHeaderIdx >= 0) rows[lastHeaderIdx].count = bucketCount;
      currentKey = dayKey;
      bucketCount = 0;
      lastHeaderIdx = rows.length;
      rows.push({ kind: "header", label: formatDayLabel(ts), count: 0 });
    }
    rows.push({ kind: "item", activity: a });
    bucketCount += 1;
  }
  if (lastHeaderIdx >= 0) rows[lastHeaderIdx].count = bucketCount;
  return rows;
}

async function copyShareLink(activityId, e) {
  // Prevent the row's parent handlers from firing.
  if (e && e.stopPropagation) e.stopPropagation();
  if (e && e.preventDefault) e.preventDefault();
  if (!activityId) {
    toast.error("This activity has no shareable id");
    return;
  }
  const url = `${window.location.origin}/activity/${activityId}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied — paste it on X");
  } catch {
    // Fallback for insecure contexts (rare, but avoids a silent failure).
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Link copied — paste it on X");
    } catch {
      toast.error("Copy failed — long-press the URL bar to copy");
    }
  }
}

/**
 * VirtualActivityFeed — windowed list (react-window) with per-day grouping.
 * Renders 1000s of rows at smooth scroll. Accepts:
 *   activities: [{id?, type, title, description, timestamp, protocol?}]
 *   height: pixel height of the scroll container (default 400)
 *   getActivityIcon(type) → React node
 *   getActivityColor(type) → tailwind class string
 */
export default function VirtualActivityFeed({
  activities,
  height = 400,
  getActivityIcon,
  getActivityColor,
}) {
  const rows = useMemo(() => groupRows(activities || []), [activities]);
  const listRef = useRef(null);

  if (!activities || activities.length === 0) {
    return (
      <div className="p-12 text-center" style={{ height }} data-testid="activity-feed-empty">
        <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
        <p className="text-sm text-muted-foreground">No activities yet</p>
      </div>
    );
  }

  const rowProps = { rows, getActivityIcon, getActivityColor };

  return (
    <div data-testid="activity-feed-virtual" style={{ height }}>
      <List
        listRef={listRef}
        rowCount={rows.length}
        rowHeight={(index) =>
          rows[index].kind === "header" ? HEADER_HEIGHT : ROW_HEIGHT
        }
        rowComponent={ActivityRow}
        rowProps={rowProps}
        overscanCount={6}
        style={{ height, width: "100%" }}
      />
    </div>
  );
}

function ActivityRow({ index, style, rows, getActivityIcon, getActivityColor }) {
  const row = rows[index];
  if (row.kind === "header") {
    return (
      <div
        style={style}
        className="px-4 py-2 bg-muted/40 border-y border-border flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground"
        data-testid={`activity-day-${index}`}
      >
        <span className="font-semibold">{row.label}</span>
        <span className="font-mono">
          {row.count} event{row.count !== 1 ? "s" : ""}
        </span>
      </div>
    );
  }
  const activity = row.activity;
  const activityId = activity.id;
  return (
    <div
      style={style}
      className="group px-4 py-3 hover:bg-muted/30 transition-colors border-b border-border"
      data-testid={`activity-${index}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-8 h-8 rounded-lg bg-muted flex items-center justify-center ${
            getActivityColor?.(activity.type) || ""
          }`}
        >
          {getActivityIcon?.(activity.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{activity.title}</p>
          <p className="text-xs text-muted-foreground truncate">
            {activity.description}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-mono">
              {activity.timestamp
                ? new Date(activity.timestamp).toLocaleTimeString()
                : ""}
            </span>
            {activity.protocol && (
              <Badge variant="outline" className="text-xs">
                {activity.protocol}
              </Badge>
            )}
          </div>
        </div>
        {/* Copy shareable-link button. Visible on hover (or always on touch
            devices via the sm:opacity-0 fallback). Uses button so keyboard
            users can tab to it. */}
        {activityId && (
          <button
            type="button"
            onClick={(e) => copyShareLink(activityId, e)}
            className="shrink-0 self-center p-2 rounded-md text-muted-foreground hover:text-orange-400 hover:bg-muted/60 transition-colors sm:opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-orange-500/40"
            title="Copy shareable link — paste it on X"
            aria-label="Copy shareable link for this activity"
            data-testid={`activity-share-btn-${index}`}
          >
            <Link2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
